import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useBusinessProfileAPI} from '@src/api/business_profile_api';
import {GenerativeModel, useGenerativeModelAPI} from '@src/api/generative_model_api';
import {BusinessProfile, Campaign, Post, Strategy} from '@src/model/business_profile';
import Alert, {AlertProps} from '@src/common/alert';
import AppContainer from '@src/common/app_container';
import BackButton from '@src/common/components/back-button';
import {ChevronRightIcon, TrashIcon} from '@heroicons/react/20/solid'

const formatDateForInput = (isoString: string | null | undefined): string => {
  if (!isoString) {
    return '';
  }
  return isoString.split('T')[0];
};

const CampaignEdit = () => {
  const { businessProfileId } = useParams();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = React.useState<Campaign[]>([]);
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(null);
  const [status, setStatus] = React.useState<AlertProps | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null);
  const [editingStrategy, setEditingStrategy] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{
    campaigns: Record<string, Partial<Campaign>>;
    strategies: Record<string, Partial<Strategy>>;
    posts: Record<string, Partial<Post>>;
  }>({
    campaigns: {},
    strategies: {},
    posts: {}
  });
  const [originalData, setOriginalData] = useState<Campaign[]>([]);
  const [availableModels, setAvailableModels] = useState<GenerativeModel[]>([]);
  const [isGeneratingCampaign, setIsGeneratingCampaign] = useState(false);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [generatingImagesForStrategy, setGeneratingImagesForStrategy] = useState<string | null>(null);
  const [addingImageForPost, setAddingImageForPost] = useState<string | null>(null);
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isBackfillModalOpen, setIsBackfillModalOpen] = useState(false);
  const [selectedBackfillCampaign, setSelectedBackfillCampaign] = useState<string | 'all'>('all');
    const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
    const [expandedStrategies, setExpandedStrategies] = useState<Set<string>>(new Set());

  const businessProfileAPI = useBusinessProfileAPI();

  const generativeModelAPI = useGenerativeModelAPI();

  useEffect(() => {
    if (businessProfileId) {
      businessProfileAPI.getBusinessProfile(businessProfileId)
        .then((profile) => {
          setBusinessProfile(profile);
          setCampaigns(profile.campaigns || []);

          if (profile.organization_id) {
            // Load available generative models for organization
            generativeModelAPI.getModelsForOrganization(profile.organization_id, true)
              .then(setAvailableModels)
              .catch(() => setAvailableModels([]));
          } else {
            // Load available generative models for users with rights to this profile
            const userIds = profile.rights?.map(right => right.user_id).filter(Boolean) || [];
            if (userIds.length > 0) {
              // For now, use the first user_id - could be enhanced to combine models from all users
              generativeModelAPI.getModelsForUser(userIds[0]!, true)
                .then(setAvailableModels)
                .catch(() => setAvailableModels([]));
            }
          }
        })
        .catch((err) => {
          setStatus({ type: "error", message: "Error loading profile: " + err.message });
        });
    }
  }, [businessProfileId]);


  const deleteCampaign = (campaignId: string) => {
    if (window.confirm("Are you sure you want to delete this campaign?")) {

      businessProfileAPI.deleteCampaign(campaignId)
        .then(() => {
          setCampaigns(campaigns.filter(campaign => campaign.id !== campaignId));
        })
        .catch((err) => {
          setStatus({ type: "error", message: "Error deleting campaign: " + err.message });
        });
    }
  }

  const generateCampaign = () => {
    if (!businessProfileId) return;

    const strategyDocuments = businessProfile?.documents?.filter(doc => doc.usage === 'STRATEGY') || [];

    if (strategyDocuments.length === 0) {
      setStatus({ type: "error", message: "No strategy documents found for this business profile" });
      return;
    }

    if (strategyDocuments.length === 1) {
      // Only one strategy document, use it directly
      generateCampaignFromDocument(strategyDocuments[0].id!);
    } else {
      // Multiple strategy documents, show selector
      setShowDocumentSelector(true);
    }
  };

  const generateCampaignFromDocument = (documentId: string) => {
    if (!businessProfileId) return;

    setShowDocumentSelector(false);

    setIsGeneratingCampaign(true);
    setStatus({ type: "info", message: "Generating campaign from strategy document..." });

    businessProfileAPI.generateCampaignFromDocument(businessProfileId, documentId)
      .then((campaign) => {
        setCampaigns(prev => [...prev, campaign]);
        setStatus({ type: "success", message: "Campaign generated!" });
        setShowDocumentSelector(false);
        setSelectedDocumentId(null);
      })
      .catch((err) => setStatus({ type: "error", message: "Error generating campaign: " + err.message }))
      .finally(() => {
        setIsGeneratingCampaign(false);
      });
  };

  const generateStrategyFromBI = () => {
    if (!businessProfileId) return;

    const biDocuments = businessProfile?.documents?.filter(doc => doc.usage === 'BUSINESS_INTELLIGENCE') || [];
    if (biDocuments.length === 0) {
      setStatus({ type: "error", message: "No Business Intelligence document found for this business profile. Run deep research first." });
      return;
    }

    setIsGeneratingStrategy(true);
    setStatus({ type: "info", message: "Generating strategy from Business Intelligence document... This may take a minute." });

    businessProfileAPI.generateStrategyFromBI(businessProfileId)
      .then((campaign) => {
        setCampaigns(prev => [...prev, campaign]);
        setStatus({ type: "success", message: "Strategy and campaign generated successfully!" });
      })
      .catch((err) => setStatus({ type: "error", message: "Error generating strategy: " + err.message }))
      .finally(() => {
        setIsGeneratingStrategy(false);
      });
  };

  const handleDocumentSelection = () => {
    if (selectedDocumentId) {
      generateCampaignFromDocument(selectedDocumentId);
    }
  };

  const handleBackfillScripts = () => {
    if (!businessProfileId) return;

    const campaignId = selectedBackfillCampaign === 'all' ? undefined : selectedBackfillCampaign;

    setStatus({ type: 'info', message: 'Starting script backfill process...' });
    businessProfileAPI.backfillScripts(businessProfileId, campaignId)
      .then((message) => {
        setStatus({ type: 'success', message: message });
        setIsBackfillModalOpen(false);
      })
      .catch((err) => {
        setStatus({ type: 'error', message: 'Error starting backfill: ' + err.message });
      });
  };

  const generateImagesForStrategy = (strategyId: string) => {
    if (businessProfileId) {
      setGeneratingImagesForStrategy(strategyId);
      setStatus({ type: "info", message: "Generating images for strategy..." });

      businessProfileAPI.generateImagesForStrategy(businessProfileId, strategyId)
        .then(() => {
          setStatus({ type: "success", message: "Images generated!" });
        })
        .catch((err: any) => setStatus({ type: "error", message: "Error generating images: " + err.message }))
        .finally(() => {
          setGeneratingImagesForStrategy(null);
        });
    }
  }

  const isAnyGenerationInProgress = () => {
    return isGeneratingCampaign || isGeneratingStrategy || generatingImagesForStrategy !== null;
  }

    const toggleCampaignExpanded = (id: string) => {
        setExpandedCampaigns(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const expandCampaign = (id: string) => {
        setExpandedCampaigns(prev => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    };

    const toggleStrategyExpanded = (id: string) => {
        setExpandedStrategies(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const expandStrategy = (id: string) => {
        setExpandedStrategies(prev => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    };

  // Helper functions for new editing system
  const enterEditMode = () => {
    setIsEditing(true);
    setOriginalData(JSON.parse(JSON.stringify(campaigns))); // Deep clone
    setPendingChanges({ campaigns: {}, strategies: {}, posts: {} });
  };

  const exitEditMode = () => {
    setIsEditing(false);
    setOriginalData([]);
    setPendingChanges({ campaigns: {}, strategies: {}, posts: {} });
    setEditingCampaignId(null);
    setEditingStrategy(null);
    setEditingPost(null);
  };

  const updatePendingCampaignChange = (campaignId: string, field: keyof Campaign, value: any) => {
    setPendingChanges(prev => ({
      ...prev,
      campaigns: {
        ...prev.campaigns,
        [campaignId]: {
          ...prev.campaigns[campaignId],
          [field]: value
        }
      }
    }));

    setCampaigns(campaigns.map(c =>
      c.id === campaignId ? { ...c, [field]: value } : c
    ));
  };

  const updatePendingStrategyChange = (strategyId: string, field: keyof Strategy, value: any) => {
    setPendingChanges(prev => ({
      ...prev,
      strategies: {
        ...prev.strategies,
        [strategyId]: {
          ...prev.strategies[strategyId],
          [field]: value
        }
      }
    }));

    setCampaigns(campaigns.map(campaign => ({
      ...campaign,
      strategies: campaign.strategies?.map(s =>
        s.id === strategyId ? { ...s, [field]: value } : s
      )
    })));
  };

  const updatePendingPostChange = (postId: string, field: keyof Post, value: any) => {
    setPendingChanges(prev => ({
      ...prev,
      posts: {
        ...prev.posts,
        [postId]: {
          ...prev.posts[postId],
          [field]: value
        }
      }
    }));

    setCampaigns(campaigns.map(campaign => ({
      ...campaign,
      strategies: campaign.strategies?.map(strategy => ({
        ...strategy,
        posts: strategy.posts?.map(p =>
          p.id === postId ? { ...p, [field]: value } : p
        )
      }))
    })));
  };

  const hasPendingChanges = () => {
    return Object.keys(pendingChanges.campaigns).length > 0 ||
      Object.keys(pendingChanges.strategies).length > 0 ||
      Object.keys(pendingChanges.posts).length > 0;
  };

  const saveAllChanges = async () => {
    try {
      setStatus({ type: "info", message: "Saving changes..." });

      // Save campaign changes
      for (const [campaignId, changes] of Object.entries(pendingChanges.campaigns)) {
        const campaign = campaigns.find(c => c.id === campaignId);
        if (campaign) {
          await businessProfileAPI.updateBusinessProfileCampaign(campaignId, { ...campaign, ...changes });
        }
      }

      // Save strategy changes
      for (const [strategyId, changes] of Object.entries(pendingChanges.strategies)) {
        const strategy = campaigns
          .flatMap(c => c.strategies || [])
          .find(s => s.id === strategyId);
        if (strategy) {
          await businessProfileAPI.updateStrategy(strategyId, { ...strategy, ...changes });
        }
      }

      // Save post changes
      for (const [postId, changes] of Object.entries(pendingChanges.posts)) {
        const post = campaigns
          .flatMap(c => c.strategies || [])
          .flatMap(s => s.posts || [])
          .find(p => p.id === postId);
        if (post) {
          await businessProfileAPI.updatePost(postId, { ...post, ...changes });
        }
      }

      exitEditMode();
      setStatus({ type: "success", message: "All changes saved successfully!" });
    } catch (err: any) {
      setStatus({ type: "error", message: "Error saving changes: " + err.message });
    }
  };

  const cancelAllChanges = () => {
    setCampaigns(originalData);
    exitEditMode();
    setStatus({ type: "info", message: "Changes cancelled" });
  };

  const updatePostGenerativeOutput = async (post: Post, generativeOutputId: string | null) => {
    try {
      const updatedPost = await businessProfileAPI.updatePostGenerativeOutput(post.id!, generativeOutputId);
      setCampaigns(campaigns.map(campaign => ({
        ...campaign,
        strategies: campaign.strategies?.map(strategy => ({
          ...strategy,
          posts: strategy.posts?.map(p =>
            p.id === post.id ? updatedPost : p
          )
        }))
      })));
      setStatus({
        type: "success",
        message: generativeOutputId ? "Image added successfully!" : "Image deleted successfully!"
      });
    } catch (err: any) {
      setStatus({
        type: "error",
        message: `Error ${generativeOutputId ? 'adding' : 'deleting'} image: ` + err.message
      });
    }
  };

  const deleteImageFromPost = async (post: Post, generativeOutputId: string | null, fileId: string | null = null) => {
    try {
      const updatedPost = await businessProfileAPI.deleteImageFromPost(post.id!, generativeOutputId, fileId);
      setCampaigns(campaigns.map(campaign => ({
        ...campaign,
        strategies: campaign.strategies?.map(strategy => ({
          ...strategy,
          posts: strategy.posts?.map(p =>
            p.id === post.id ? updatedPost : p
          )
        }))
      })));
      setStatus({
        type: "success",
        message: generativeOutputId ? "Image added successfully!" : "Image deleted successfully!"
      });
    } catch (err: any) {
      setStatus({
        type: "error",
        message: `Error ${generativeOutputId ? 'adding' : 'deleting'} image: ` + err.message
      });
    }
  };

  const deleteAllImagesFromPost = async (post: Post) => {
    try {
      const updatedPost = await businessProfileAPI.deleteAllImagesFromPost(post.id!);
      setCampaigns(campaigns.map(campaign => ({
        ...campaign,
        strategies: campaign.strategies?.map(strategy => ({
          ...strategy,
          posts: strategy.posts?.map(p =>
            p.id === post.id ? updatedPost : p
          )
        }))
      })));
      setStatus({
        type: "success",
        message: "All images deleted successfully!"
      });
    } catch (err: any) {
      setStatus({
        type: "error",
        message: "Error deleting all images: " + err.message
      });
    }
  };


  return (
    <AppContainer>
      <div className="space-y-4">
        <BackButton variant="ghost" onClick={() => navigate(`/business-profiles`)} />
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl font-bold">Manage Campaign for {businessProfile?.name}</h1>
          <div className="flex space-x-2">
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={() => setShowAddForm(true)}
              disabled={isAnyGenerationInProgress() || isEditing}
            >
              Add Campaign
            </button>
            <button
              onClick={generateStrategyFromBI}
              className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isAnyGenerationInProgress() || isEditing}
            >
              {isGeneratingStrategy && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isGeneratingStrategy ? 'Generating Strategy...' : 'Generate Strategy from BI'}
            </button>
            <button
              onClick={generateCampaign}
              className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isAnyGenerationInProgress() || isEditing}
            >

              {isGeneratingCampaign && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isGeneratingCampaign ? 'Generating Campaign...' : 'Generate Campaign From Strategy Document'}
            </button>
            <button
              onClick={() => setIsBackfillModalOpen(true)}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={isAnyGenerationInProgress() || isEditing}
            >
              Add Scripts to Posts
            </button>
          </div>
        </div>
        {status && <Alert {...status} />}

        {/* Document Selection Modal */}
        {showDocumentSelector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Select Strategy Document</h3>
              <p className="text-gray-600 mb-4">
                Multiple strategy documents found. Please select which one to use for campaign generation:
              </p>
              <div className="space-y-2 mb-4">
                {businessProfile?.documents
                  ?.filter(doc => doc.usage === 'STRATEGY')
                  .map((doc) => (
                    <label key={doc.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="documentSelection"
                        value={doc.id}
                        checked={selectedDocumentId === doc.id}
                        onChange={(e) => setSelectedDocumentId(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-sm">{doc.name || `Strategy Document ${doc.id}`}</span>
                    </label>
                  ))}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleDocumentSelection}
                  disabled={!selectedDocumentId || isGeneratingCampaign}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isGeneratingCampaign ? 'Generating...' : 'Generate Campaign'}
                </button>
                <button
                  onClick={() => {
                    setShowDocumentSelector(false);
                    setSelectedDocumentId(null);
                  }}
                  disabled={isGeneratingCampaign}
                  className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded disabled:bg-gray-200 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Backfill Scripts Modal */}
        {isBackfillModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Add Scripts to Posts</h3>
              <p className="text-gray-600 mb-4">
                This will generate a lip-sync script for posts that have images with people in them.
                Select which posts to generate scripts for:
              </p>
              <div className="space-y-2 mb-4">
                <select
                  value={selectedBackfillCampaign}
                  onChange={(e) => setSelectedBackfillCampaign(e.target.value)}
                  className="w-full border border-gray-300 rounded p-2"
                >
                  <option value="all">All Posts</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id!}>
                      Campaign: {campaign.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleBackfillScripts}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Start Process
                </button>
                <button
                  onClick={() => setIsBackfillModalOpen(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4">
          {showAddForm && (
            <div className="mb-4 p-4 border rounded bg-gray-50">
              <p className="text-gray-600 mb-4">Use "Generate Campaign From Strategy Document" to create campaigns with AI, or campaigns will appear here when created.</p>
              <button
                onClick={() => setShowAddForm(false)}
                className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-1 px-4 rounded disabled:bg-gray-200 disabled:cursor-not-allowed"
                disabled={isAnyGenerationInProgress()}
              >
                Close
              </button>
            </div>
          )}
          {campaigns.length === 0 ? <div>No campaigns found</div> :
            <div className="pt-4">
                {campaigns.map((campaign) => {
                    const isCampaignExpanded = campaign.id ? expandedCampaigns.has(campaign.id) : false;
                    return (
                <div key={campaign.id} className={`border border-gray-300 rounded-lg mb-4 p-4 campaign-edit-${campaign.id}`}>
                  <div className="w-full flex justify-between items-start">
                      <button
                          type="button"
                          onClick={() => campaign.id && toggleCampaignExpanded(campaign.id)}
                          aria-label={isCampaignExpanded ? 'Collapse campaign' : 'Expand campaign'}
                          aria-expanded={isCampaignExpanded}
                          className="mr-2 mt-1 p-1 rounded hover:bg-gray-100 flex-shrink-0"
                      >
                          <ChevronRightIcon
                              className={`h-5 w-5 transition-transform ${isCampaignExpanded ? 'rotate-90' : ''}`}
                          />
                      </button>
                    <div className="flex-1">
                      {/* Inline editable campaign name */}
                      {isEditing && editingCampaignId === campaign.id ? (
                        <input
                          className="text-xl font-bold border-b-2 border-blue-500 bg-transparent w-full"
                          value={campaign.name || ''}
                          onChange={(e) => {
                            updatePendingCampaignChange(campaign.id!, 'name', e.target.value);
                          }}
                          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              setEditingCampaignId(null);
                            }
                          }}
                          autoFocus
                          disabled={isAnyGenerationInProgress()}
                        />
                      ) : (
                        <h3
                          className={`text-xl font-bold p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                          onClick={() => {
                            if (!isAnyGenerationInProgress() && !isEditing) {
                                expandCampaign(campaign.id!);
                              enterEditMode();
                              setEditingCampaignId(campaign.id!);
                            }
                          }}
                        >
                          {campaign.name}
                        </h3>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                        onClick={() => deleteCampaign(campaign.id || '')}
                        disabled={isAnyGenerationInProgress() || isEditing}
                      >
                        Delete Campaign
                      </button>
                    </div>
                  </div>

                    {isCampaignExpanded && (
                  <div className="ml-2 mt-4" onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setEditingCampaignId(null);
                    }
                  }}>
                    {/* Inline editable summary */}
                    <div className="mb-4">
                      <p className="font-bold">Summary:</p>
                      {isEditing && editingCampaignId === campaign.id ? (
                        <textarea
                          className="w-full border border-gray-300 rounded p-2 mt-1"
                          value={campaign.summary || ''}
                          onChange={(e) => {
                            updatePendingCampaignChange(campaign.id!, 'summary', e.target.value);
                          }}
                          rows={3}
                          disabled={isAnyGenerationInProgress()}
                        />
                      ) : (
                        <p
                          className={`p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                          onClick={() => {
                            if (!isAnyGenerationInProgress() && !isEditing) {
                              enterEditMode();
                              setEditingCampaignId(campaign.id!);
                            }
                          }}
                        >
                          {campaign.summary || 'Click to add summary...'}
                        </p>
                      )}
                    </div>

                    {/* Inline editable goal */}
                    <div className="mb-4">
                      <p className="font-bold">Goal:</p>
                      {isEditing && editingCampaignId === campaign.id ? (
                        <input
                          className="w-full border border-gray-300 rounded p-2 mt-1"
                          value={campaign.goal || ''}
                          onChange={(e) => {
                            updatePendingCampaignChange(campaign.id!, 'goal', e.target.value);
                          }}
                          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              setEditingCampaignId(null);
                            }
                          }}
                          disabled={isAnyGenerationInProgress()}
                        />
                      ) : (
                        <p
                          className={`p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                          onClick={() => {
                            if (!isAnyGenerationInProgress() && !isEditing) {
                              enterEditMode();
                              setEditingCampaignId(campaign.id!);
                            }
                          }}
                        >
                          {campaign.goal || 'Click to add goal...'}
                        </p>
                      )}
                    </div>

                    {/* Inline editable dates */}
                    <div className="mb-4">
                      <p className="font-bold">Duration:</p>
                      {isEditing && editingCampaignId === campaign.id ? (
                        <div className="flex gap-2 mt-1">
                          <input
                            type="date"
                            className="border border-gray-300 rounded p-1"
                            value={formatDateForInput(campaign.start_date)}
                            onChange={(e) => {
                              const finalValue = e.target.value ? `${e.target.value}T00:00:00.000Z` : '';
                              updatePendingCampaignChange(campaign.id!, 'start_date', finalValue);
                            }}
                          />
                          <span>to</span>
                          <input
                            type="date"
                            className="border border-gray-300 rounded p-1"
                            value={formatDateForInput(campaign.end_date)}
                            onChange={(e) => {
                              const finalValue = e.target.value ? `${e.target.value}T00:00:00.000Z` : '';
                              updatePendingCampaignChange(campaign.id!, 'end_date', finalValue);
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          className={`p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                          onClick={() => {
                            if (!isAnyGenerationInProgress() && !isEditing) {
                              enterEditMode();
                              setEditingCampaignId(campaign.id!);
                            }
                          }}
                        >
                          {campaign.start_date && campaign.end_date
                            ? `${formatDateForInput(campaign.start_date)} - ${formatDateForInput(campaign.end_date)}`
                            : 'Click to set dates...'}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-between">
                      <p className="font-bold">Weekly Strategies:</p>
                    </div>
                      {(campaign.strategies || []).map((strategy, strategyIndex) => {
                          const isStrategyExpanded = strategy.id ? expandedStrategies.has(strategy.id) : false;
                          return (
                      <div key={strategy.id || strategyIndex} className="border-2 border-blue-500 rounded-lg p-3 mb-3 bg-white" onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                          setEditingStrategy(null);
                        }
                      }}>
                        <div className="flex justify-between items-center mb-2">
                            <button
                                type="button"
                                onClick={() => strategy.id && toggleStrategyExpanded(strategy.id)}
                                aria-label={isStrategyExpanded ? 'Collapse strategy' : 'Expand strategy'}
                                aria-expanded={isStrategyExpanded}
                                className="mr-2 p-1 rounded hover:bg-gray-100 flex-shrink-0"
                            >
                                <ChevronRightIcon
                                    className={`h-4 w-4 transition-transform ${isStrategyExpanded ? 'rotate-90' : ''}`}
                                />
                            </button>
                          {/* Inline editable strategy name */}
                          {isEditing && editingStrategy === strategy.id ? (
                            <input
                              className="text-lg font-bold border-b-2 border-blue-500 bg-transparent flex-1"
                              value={strategy.name || ''}
                              onChange={(e) => {
                                updatePendingStrategyChange(strategy.id!, 'name', e.target.value);
                              }}
                              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  setEditingStrategy(null);
                                }
                              }}
                              autoFocus
                              disabled={isAnyGenerationInProgress()}
                            />
                          ) : (
                            <h4
                              className={`text-lg font-bold p-1 rounded flex-1 ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                              onClick={() => {
                                if (!isAnyGenerationInProgress() && !isEditing) {
                                    expandStrategy(strategy.id!);
                                  enterEditMode();
                                  setEditingStrategy(strategy.id!);
                                }
                              }}
                            >
                              {strategy.name || 'Click to add name...'}
                            </h4>
                          )}
                          <button
                            onClick={() => generateImagesForStrategy(strategy.id || '')}
                            className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-1 px-3 rounded text-sm ml-2 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                            disabled={isAnyGenerationInProgress()}
                          >
                            {generatingImagesForStrategy === strategy.id && (
                              <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            )}
                            {generatingImagesForStrategy === strategy.id ? 'Generating...' : 'Generate Images'}
                          </button>
                        </div>

                          {isStrategyExpanded && (<>
                        {/* Inline editable strategy description */}
                        {isEditing && editingStrategy === strategy.id ? (
                          <textarea
                            className="w-full border border-gray-300 rounded p-2 mb-2"
                            value={strategy.description || ''}
                            onChange={(e) => {
                              updatePendingStrategyChange(strategy.id!, 'description', e.target.value);
                            }}
                            rows={2}
                            placeholder="Strategy description..."
                            disabled={isAnyGenerationInProgress()}
                          />
                        ) : (
                          <p
                            className={`text-sm p-1 rounded mb-2 ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                            onClick={() => {
                              if (!isAnyGenerationInProgress() && !isEditing) {
                                enterEditMode();
                                setEditingStrategy(strategy.id!);
                              }
                            }}
                          >
                            <span className="font-medium">Description:</span> {strategy.description || 'Click to add description...'}
                          </p>
                        )}

                        {/* Strategy dates */}
                        <div className="mb-2">
                          <p className="font-bold text-sm">Strategy Dates:</p>
                          {isEditing && editingStrategy === strategy.id ? (
                            <div className="flex gap-2 mt-1">
                              <input
                                type="date"
                                className="border border-gray-300 rounded p-1 text-sm"
                                value={formatDateForInput(strategy.start_date)}
                                onChange={(e) => {
                                  const finalValue = e.target.value ? `${e.target.value}T00:00:00.000Z` : '';
                                  updatePendingStrategyChange(strategy.id!, 'start_date', finalValue);
                                }}
                              />
                              <span className="text-sm">to</span>
                              <input
                                type="date"
                                className="border border-gray-300 rounded p-1 text-sm"
                                value={formatDateForInput(strategy.end_date)}
                                onChange={(e) => {
                                  const finalValue = e.target.value ? `${e.target.value}T00:00:00.000Z` : '';
                                  updatePendingStrategyChange(strategy.id!, 'end_date', finalValue);
                                }}
                              />
                            </div>
                          ) : (
                            <div
                              className={`text-sm p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                              onClick={() => {
                                if (!isAnyGenerationInProgress() && !isEditing) {
                                  enterEditMode();
                                  setEditingStrategy(strategy.id!);
                                }
                              }}
                            >
                              {strategy.start_date && strategy.end_date
                                ? `${formatDateForInput(strategy.start_date)} - ${formatDateForInput(strategy.end_date)}`
                                : 'Click to set dates...'}
                            </div>
                          )}
                        </div>

                        {/* Posts */}
                        {strategy.posts && strategy.posts.length > 0 && (
                          <div className="space-y-4">
                            <h5 className="font-bold">Posts</h5>
                            {strategy.posts.map((post) => (
                              <div key={post.id} className="border-t border-gray-300 pt-3">
                                {/* Post Images Section */}
                                <span className="font-bold text-sm">Post Images: </span>
                                {post.generative_outputs && post.generative_outputs.length > 0 ? (
                                  <div className="flex flex-row overflow-x-auto gap-2 mb-3">
                                    {post.generative_outputs.map((output) => (
                                      <div key={output.generative_output_id} style={{ position: 'relative' }}>
                                        <img src={output.permalink} alt="Generative output" className="max-w-xs" />
                                        <button
                                          onClick={() => deleteImageFromPost(post, output.generative_output_id, output.file_id)}
                                          className="bg-red-500 hover:bg-red-700 text-white text-xs px-2 py-1 rounded mt-1 w-[40px] h-[40px] absolute bottom-1 right-1"
                                          disabled={isAnyGenerationInProgress()}
                                        ><TrashIcon /></button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm font-bold text-gray-600 mb-3">No images generated yet</p>
                                )}

                                {/* Image Management Buttons */}
                                <div className="mb-3">
                                  <div className="flex items-center gap-2">
                                    {addingImageForPost === post.id ? (
                                      <>
                                        <input
                                          type="text"
                                          placeholder="Enter generative output ID"
                                          className="border border-gray-300 rounded p-1 text-sm w-full"
                                          id={`add-image-${post.id}`}
                                          disabled={isAnyGenerationInProgress()}
                                          autoFocus
                                        />
                                        <button
                                          onClick={() => {
                                            const input = document.getElementById(`add-image-${post.id}`) as HTMLInputElement;
                                            const generativeOutputId = input.value.trim();
                                            if (generativeOutputId) {
                                              updatePostGenerativeOutput(post, generativeOutputId);
                                              input.value = '';
                                            }
                                            setAddingImageForPost(null);
                                          }}
                                          className="bg-blue-500 hover:bg-blue-700 text-white text-sm px-3 py-1 rounded"
                                          disabled={isAnyGenerationInProgress()}
                                        >
                                          Submit
                                        </button>
                                        <button
                                          onClick={() => {
                                            const input = document.getElementById(`add-image-${post.id}`) as HTMLInputElement;
                                            input.value = '';
                                            setAddingImageForPost(null);
                                          }}
                                          className="bg-gray-500 hover:bg-gray-700 text-white text-sm px-3 py-1 rounded"
                                          disabled={isAnyGenerationInProgress()}
                                        >
                                          Cancel
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => setAddingImageForPost(post.id!)}
                                        className="bg-green-500 hover:bg-green-700 text-white text-sm px-3 py-1 rounded"
                                        disabled={isAnyGenerationInProgress()}
                                      >
                                        Add Image
                                      </button>
                                    )}

                                    {post.generative_outputs && post.generative_outputs.length > 0 && (
                                      <button
                                        onClick={() => deleteAllImagesFromPost(post)}
                                        className="bg-red-500 hover:bg-red-700 text-white text-sm px-3 py-1 rounded"
                                        disabled={isAnyGenerationInProgress()}
                                      >
                                        Delete All Images
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Inline editable post date */}
                                <div className="mb-2">
                                  <span className="font-bold text-sm">Post Date: </span>
                                  {isEditing && editingPost === post.id ? (
                                    <input
                                      type="date"
                                      className="border border-gray-300 rounded p-1 text-sm"
                                      value={formatDateForInput(post.post_date)}
                                      onChange={(e) => {
                                        const finalValue = e.target.value ? `${e.target.value}T00:00:00.000Z` : '';
                                        updatePendingPostChange(post.id!, 'post_date', finalValue)
                                      }}
                                      onBlur={() => setEditingPost(null)}
                                      disabled={isAnyGenerationInProgress()}
                                    />
                                  ) : (
                                    <span
                                      className={`text-sm p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                                      onClick={() => {
                                        if (!isAnyGenerationInProgress() && !isEditing) {
                                          enterEditMode();
                                          setEditingPost(post.id!);
                                        }
                                      }}
                                    >
                                      {formatDateForInput(post.post_date) || 'Click to set date...'}
                                    </span>
                                  )}
                                </div>

                                {/* Social account provider selector */}
                                <div className="mb-2">
                                  <span className="font-bold text-sm">Social Platform: </span>
                                  <select
                                    className="border border-gray-300 rounded p-1 text-sm ml-1 w-full"
                                    value={post.social_account_provider || ''}
                                    onChange={(e) => {
                                      if (isEditing) {
                                        updatePendingPostChange(post.id!, 'social_account_provider', e.target.value);
                                      } else {
                                        enterEditMode();
                                        updatePendingPostChange(post.id!, 'social_account_provider', e.target.value);
                                      }
                                    }}
                                    disabled={isAnyGenerationInProgress()}
                                  >
                                    <option value="">Select platform...</option>
                                    <option value="Instagram">Instagram</option>
                                    <option value="TikTok">TikTok</option>
                                    <option value="Twitter">Twitter</option>
                                    <option value="YouTube">YouTube</option>
                                    <option value="Facebook">Facebook</option>
                                    <option value="LinkedIn">LinkedIn</option>
                                    <option value="Twitch">Twitch</option>
                                    <option value="Patreon">Patreon</option>
                                  </select>
                                </div>

                                {/* Inline editable prompt */}
                                <div className="mb-2">
                                  <span className="font-bold text-sm">Prompt: </span>
                                  {isEditing && editingPost === post.id ? (
                                    <textarea
                                      className="w-full border border-gray-300 rounded p-1 text-sm mt-1"
                                      value={post.prompt || ''}
                                      onChange={(e) => updatePendingPostChange(post.id!, 'prompt', e.target.value)}
                                      onBlur={() => setEditingPost(null)}
                                      rows={2}
                                      disabled={isAnyGenerationInProgress()}
                                    />
                                  ) : (
                                    <span
                                      className={`text-sm p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                                      onClick={() => {
                                        if (!isAnyGenerationInProgress() && !isEditing) {
                                          enterEditMode();
                                          setEditingPost(post.id!);
                                        }
                                      }}
                                    >
                                      {post.prompt || 'Click to add prompt...'}
                                    </span>
                                  )}
                                </div>

                                {/* Generative model selection */}
                                <div className="mb-2 flex flex-row gap-2">
                                  <span className="font-bold text-sm">Generative model: </span>
                                  {isEditing && editingPost === post.id ?
                                    <select
                                      value={post.generative_model_id || ''}
                                      onChange={(e) => updatePendingPostChange(post.id!, 'generative_model_id', e.target.value)}
                                      className="border border-gray-300 rounded p-1 text-sm"
                                      disabled={isAnyGenerationInProgress()}
                                    >
                                      <option value="">Select model...</option>
                                      {availableModels.map((model) => (
                                        <option key={model.id} value={model.id}>
                                          {model.name}
                                        </option>
                                      ))}
                                    </select>
                                    : <p
                                      className={`text-sm ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100 p-1 rounded'}`}
                                      onClick={() => {
                                        if (!isAnyGenerationInProgress() && !isEditing) {
                                          enterEditMode();
                                          setEditingPost(post.id!);
                                        }
                                      }}
                                    >
                                      {post.generative_model_id ? post.generative_model_id : 'No model selected - click to select'}
                                    </p>
                                  }
                                </div>

                                {/* Inline editable caption */}
                                <div className="mb-2">
                                  <span className="font-bold text-sm">Caption: </span>
                                  {isEditing && editingPost === post.id ? (
                                    <textarea
                                      className="w-full border border-gray-300 rounded p-1 text-sm mt-1"
                                      value={post.caption || ''}
                                      onChange={(e) => updatePendingPostChange(post.id!, 'caption', e.target.value)}
                                      onBlur={() => setEditingPost(null)}
                                      rows={2}
                                      disabled={isAnyGenerationInProgress()}
                                    />
                                  ) : (
                                    <span
                                      className={`text-sm p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                                      onClick={() => {
                                        if (!isAnyGenerationInProgress() && !isEditing) {
                                          enterEditMode();
                                          setEditingPost(post.id!);
                                        }
                                      }}
                                    >
                                      {post.caption || 'Click to add caption...'}
                                    </span>
                                  )}
                                </div>

                                {/* Inline editable script */}
                                <div className="mb-2">
                                  <span className="font-bold text-sm">Script: </span>
                                  {isEditing && editingPost === post.id ? (
                                    <textarea
                                      className="w-full border border-gray-300 rounded p-1 text-sm mt-1"
                                      value={post.script || ''}
                                      onChange={(e) => updatePendingPostChange(post.id!, 'script', e.target.value)}
                                      onBlur={() => setEditingPost(null)}
                                      rows={2}
                                      disabled={isAnyGenerationInProgress()}
                                      placeholder="Lip-sync script will be generated here..."
                                    />
                                  ) : (
                                    <span
                                      className={`text-sm p-1 rounded ${isAnyGenerationInProgress() || isEditing ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                                      onClick={() => {
                                        if (!isAnyGenerationInProgress() && !isEditing) {
                                          enterEditMode();
                                          setEditingPost(post.id!);
                                        }
                                      }}
                                    >
                                      {post.script || 'No script generated.'}
                                    </span>
                                  )}
                                </div>

                                <p className="text-sm text-gray-600">
                                  <span className="font-bold">User rating:</span> {post.score ? post.score : 'Not rated'}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                          </>)}
                      </div>
                          );
                      })}
                  </div>
                    )}
                </div>
                    );
                })}
            </div>
          }
        </div>
      </div>

      {/* Sticky Save/Cancel Buttons */}
      {isEditing && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 shadow-lg p-4 z-50">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {hasPendingChanges() ? 'You have unsaved changes' : 'Edit mode active'}
              </span>
              {hasPendingChanges() && (
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelAllChanges}
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isAnyGenerationInProgress()}
              >
                Cancel
              </button>
              <button
                onClick={saveAllChanges}
                className={`font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed ${hasPendingChanges()
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                disabled={isAnyGenerationInProgress() || !hasPendingChanges()}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

    </AppContainer>
  );
};

export default CampaignEdit;